import { useCallback, useEffect, useState, useContext } from 'react'
import { startOfToday } from 'date-fns'
import { Card } from '@aptive-env/storybook'
import MiniCalendar from 'components/shared/MiniCalendar'
import CalendarDayList from 'components/CalendarDayList'
import { ICalendarDate } from 'interface/calendar'
import { Ioffice, Iregion } from 'interface'
import {
  fetchCalendarEventTypes,
  fetchCurrentWeekCalendarEventsWithWeatherForecasts,
  fetchOfficeEmployees,
} from 'services/calendar'
import { AptiveIcon, Toast } from '@aptive-env/storybook'
import { fetchRegions } from 'services/track'
import { OfficeModal } from 'components/OfficeModal'
import { EventUpdateModal } from 'components/EventUpdateModal'
import OfficeCalendar from 'components/OfficeCalendar'
import { CalendarStateContext } from 'context/CalendarContext'
import { eventConstants } from 'constants/index'

const { EVENT_NEW_TYPE } = eventConstants

interface Status {
  error: string | null,
  success: string | null
}

const Calendar: React.FC = () => {
  const {
    eventTypes,
    setEventTypes,
    selectedOffice,
    setSelectedOffice,
    setOfficeEmployees,
    calendarMode,
    setCalendarMode,
  } = useContext(CalendarStateContext)

  const [regions, setRegions] = useState<Iregion[]>([])
  const [currentDate, setCurrentDate] = useState<Date>(startOfToday())
  const [currentWeekDates, setCurrentWeekDates] = useState<
    Array<ICalendarDate>
  >([])
  const [status, setStatus] = useState<Status>({ error: null, success: null })
  const [isNewEventModalOpen, setIsNewEventModalOpen] = useState(false)
  const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false)
  const [officeCalendarKey, setOfficeCalendarKey] = useState(0)

  const handleDateChange = useCallback(
    (date: Date) => setCurrentDate(date),
    []
  )

  const openOfficeSelectionModal = () => {
    setIsOfficeModalOpen(true)
  }

  const closeOfficeSelectionModal = () => {
    setIsOfficeModalOpen(false)
  }

  const handleChangeOffice = (office: Ioffice) => {
    setSelectedOffice(office)
    closeOfficeSelectionModal()
  }

  const openNewEventModal = () => {
    setIsNewEventModalOpen(true)
  }

  const closeNewEventModal = () => {
    setIsNewEventModalOpen(false)
  }

  const handleEventSuccess = (success: string) => {
    setStatus({...status, success: success})
    closeNewEventModal()
    fetchCurrentWeekCalendarEventsWithWeatherForecasts(selectedOffice)
      .then(({ data, error }) => {
        setCurrentWeekDates(data)
        setStatus({ ...status, error: error })
      })
      .catch((e) => {
        console.log(e)
      })
    setOfficeCalendarKey(officeCalendarKey + 1)
  }

  useEffect(() => {
    if (selectedOffice) {
      fetchCurrentWeekCalendarEventsWithWeatherForecasts(selectedOffice)
        .then(({ data, error }) => {
          setCurrentWeekDates(data)
          setStatus({ ...status, error: error })
        })
        .catch((e) => {
          console.log(e)
        })

      fetchOfficeEmployees(selectedOffice.id)
        .then((items) => {
          setOfficeEmployees(items)
        })
        .catch((error) => {
          console.log(error)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOffice])

  useEffect(() => {
    fetchRegions()
      .then((regions: any) => {
        setRegions(regions)
      })
      .catch((error) => {
        console.log(error)
      })

    fetchCalendarEventTypes()
      .then((items) => {
        setEventTypes(items)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [setEventTypes])

  return (
    <div className="flex h-screen bg-white overflow-hidden p-3">
      <div className="w-[330px] p-3 box-content flex-none flex flex-col">
        <Card className="bg-gray-50 shadow-med rounded-lg">
          <div className="pl-4 pr-2 text-ui-gray-600 flex items-center justify-between">
            <div className="mt-4 mb-2">
              <div className="text-base leading-none text-text-default mb-2">
                Office: {selectedOffice.region} /
              </div>
              <div className="text-xl leading-6 text-text-default">
                {selectedOffice.name}
              </div>
            </div>
            <div
              className="flex items-center justify-center h-[40px] cursor-pointer"
              onClick={openOfficeSelectionModal}
            >
              <AptiveIcon
                className="w-[24px] h-[24px] stroke-ui-gray-600 fill-none"
                icon="globeAlt"
              />
              <div className="w-[16px] h-[24px] flex flex-col items-center relative">
                <AptiveIcon
                  className="w-[16px] h-[12px] stroke-ui-gray-600 fill-none absolute bottom-2.5"
                  icon="chevronUp"
                />
                <AptiveIcon
                  className="w-[16px] h-[12px] stroke-ui-gray-600 fill-none absolute top-2.5"
                  icon="chevronDown"
                />
              </div>
            </div>
          </div>
        </Card>
        <Card className="py-4 px-[31px]">
          <MiniCalendar
            className="px-2 py-4"
            currentDate={currentDate}
            view="month"
            onChange={handleDateChange}
          />
        </Card>
        <CalendarDayList data={currentWeekDates} />
      </div>
      <OfficeCalendar
        officeId={selectedOffice.id}
        key={officeCalendarKey}
        onClickAddNewEvent={openNewEventModal}
        onSuccess={handleEventSuccess}
        eventTypes={eventTypes}
        activeDate={currentDate}
        viewMode={calendarMode}
        setViewMode={setCalendarMode}
      />
      <OfficeModal
        open={isOfficeModalOpen}
        className="w-[658px] min-h-599"
        selectedOffice={selectedOffice}
        regions={regions}
        onSelectOffice={handleChangeOffice}
        onClose={closeOfficeSelectionModal}
        center={selectedOffice.location}
      />
      {isNewEventModalOpen && (
        <EventUpdateModal
          isOpen={isNewEventModalOpen}
          setIsOpen={setIsNewEventModalOpen}
          eventParticipants={[]}
          updateType={EVENT_NEW_TYPE}
          onClose={closeNewEventModal}
          onSuccess={handleEventSuccess}
        />
      )}
      <div className="fixed top-3 right-3 flex flex-col gap-2 z-[100]">
        {status.error && (
          <Toast
            dismissible
            isIcon
            onDismiss={() => setStatus({ ...status, error: null })}
            status="danger"
          >
            {status.error}
          </Toast>
        )}
        {status.success && (
          <Toast
            dismissible
            isIcon
            onDismiss={() => setStatus({ ...status, success: null })}
            status="success"
          >
            {status.success}
          </Toast>
        )}
      </div>
    </div>
  )
}

export default Calendar
